import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import gsap from 'gsap';
import { useSlobozip } from '@/lib/useSlobozip';
import { parseSlbz, type SlbzAnatomy, type SlbzField } from '@/lib/slbz';
import { DUR, EASE_OUT_QUINT, GSAP_EASE } from '@/lib/motion';
import { StaggerHeading } from '@/components/StaggerHeading';

/**
 * The default specimen text — a fragment of the project's README-ish prose
 * that compresses well thanks to repeats. Becomes a single-file .slbz once
 * WASM is ready, parsed into the anatomy figure.
 */
const SPECIMEN_TEXT = `Lossless compression replaces repeats with references.
Every time the encoder sees a byte sequence it has seen before
inside its sliding window, it writes a back-reference (offset,length)
instead of the literal bytes. References are short, literals are not.
Repeats are short. Literals are not. References shrink the stream.`;

const SPECIMEN_NAME = 'specimen.txt';

/**
 * Anatomy — Plate I.
 *
 * Drenched coral plate. A real .slbz archive (built live from the
 * specimen text via WASM) is rendered as a horizontal byte ribbon
 * with a synced legend below. Hover any legend row → its bytes glow
 * in the ribbon. Drop your own .slbz to reparse on the fly.
 */
export function Anatomy() {
  const wasm = useSlobozip();
  const [archive, setArchive] = useState<{ name: string; data: Uint8Array } | null>(null);
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [pinnedId, setPinnedId] = useState<string | null>(null);
  // Pinned wins so a tap on touch survives the hover-leave that
  // immediately follows on a mouse-emulated tap.
  const activeId = pinnedId ?? hoverId;
  const togglePinned = (id: string) =>
    setPinnedId((cur) => (cur === id ? null : id));
  const fileInputRef = useRef<HTMLInputElement>(null);
  const figureRef = useRef<HTMLDivElement>(null);

  // Build the default specimen archive once WASM is ready.
  useEffect(() => {
    if (wasm.status !== 'ready' || archive) return;
    try {
      const data = wasm.api.createArchive([
        { name: SPECIMEN_NAME, data: new TextEncoder().encode(SPECIMEN_TEXT) },
      ]);
      setArchive({ name: 'specimen.slbz', data });
    } catch {
      /* graceful: anatomy stays in skeleton state */
    }
  }, [wasm, archive]);

  const anatomy: SlbzAnatomy | null = useMemo(
    () => (archive ? parseSlbz(archive.data) : null),
    [archive],
  );

  const onPickArchive = async (file: File) => {
    const buf = await file.arrayBuffer();
    setArchive({ name: file.name, data: new Uint8Array(buf) });
  };

  return (
    <section
      id="anatomy"
      style={{
        padding: '160px 0',
        background: 'var(--plate-bg)',
        color: 'var(--plate-text)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Atmosphere — graph-paper grid baked into the plate.
       *  Honest to the "exploded blueprint" framing of the section.
       *  Major lines every 80px, minor every 16px. Plate-text colour
       *  with very low opacity so it disappears at a glance. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(to right, color-mix(in oklch, var(--plate-text) 8%, transparent) 1px, transparent 1px),
            linear-gradient(to bottom, color-mix(in oklch, var(--plate-text) 8%, transparent) 1px, transparent 1px),
            linear-gradient(to right, color-mix(in oklch, var(--plate-text) 4%, transparent) 1px, transparent 1px),
            linear-gradient(to bottom, color-mix(in oklch, var(--plate-text) 4%, transparent) 1px, transparent 1px)
          `,
          backgroundSize: '80px 80px, 80px 80px, 16px 16px, 16px 16px',
          backgroundPosition: '0 0, 0 0, 0 0, 0 0',
          maskImage:
            'radial-gradient(ellipse at center, black 40%, transparent 95%)',
          WebkitMaskImage:
            'radial-gradient(ellipse at center, black 40%, transparent 95%)',
          pointerEvents: 'none',
        }}
      />
      <div style={{ position: 'relative', zIndex: 1 }}>
      <div className="container-x">
        {/* Eyebrow + heading */}
        <span
          className="t-mono"
          style={{
            fontSize: 'var(--type-xs)',
            color: 'color-mix(in oklch, var(--plate-text) 70%, transparent)',
            letterSpacing: 'var(--tracking-mono-caps)',
            textTransform: 'uppercase',
          }}
        >
          plate i
        </span>
        <StaggerHeading
          as="h2"
          className="t-heading"
          stagger={0.04}
          style={{
            marginTop: 16,
            fontSize: 'var(--type-xl)',
            color: 'var(--plate-text)',
            maxWidth: '20ch',
          }}
        >
          Anatomy of a{' '}
          <span className="t-italic-accent" style={{ color: 'var(--plate-text)' }}>
            .slbz.
          </span>
        </StaggerHeading>
        <p
          style={{
            marginTop: 20,
            fontSize: 'var(--type-md)',
            color: 'color-mix(in oklch, var(--plate-text) 85%, transparent)',
            maxWidth: '60ch',
            lineHeight: 1.55,
          }}
        >
          One annotated cross-section of a real archive. Twelve bytes of
          header, an entry block, then the LZSS stream. Hover or tap a field
          to find it on the ribbon. Drop your own to parse it live.
        </p>

        {/* Action row */}
        <div
          className="t-mono"
          style={{
            marginTop: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
            fontSize: 'var(--type-xs)',
            letterSpacing: 'var(--tracking-mono-caps)',
            textTransform: 'uppercase',
            color: 'color-mix(in oklch, var(--plate-text) 75%, transparent)',
          }}
        >
          <span>
            specimen ·{' '}
            <span style={{ color: 'var(--plate-text)' }}>
              {archive ? archive.name : 'building…'}
            </span>
            {anatomy && (
              <>
                {' '}
                ·{' '}
                <span style={{ color: 'var(--plate-text)' }}>
                  {anatomy.totalSize} bytes
                </span>
              </>
            )}
          </span>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={wasm.status !== 'ready'}
            style={{
              padding: '10px 18px',
              border: '1px solid color-mix(in oklch, var(--plate-text) 35%, transparent)',
              borderRadius: 'var(--radius-pill)',
              color: 'var(--plate-text)',
              background: 'transparent',
              cursor: wasm.status !== 'ready' ? 'not-allowed' : 'pointer',
              transition:
                'background var(--dur-fast) var(--ease-out-expo), border-color var(--dur-fast) var(--ease-out-expo)',
            }}
            onMouseEnter={(e) => {
              if (wasm.status === 'ready') {
                e.currentTarget.style.background = 'color-mix(in oklch, var(--plate-text) 12%, transparent)';
                e.currentTarget.style.borderColor = 'var(--plate-text)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor =
                'color-mix(in oklch, var(--plate-text) 35%, transparent)';
            }}
          >
            load your own .slbz ↑
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".slbz"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onPickArchive(f);
            }}
          />
        </div>

        {/* Ribbon + legend */}
        <AnimatePresence mode="wait">
          {anatomy ? (
            <motion.div
              key="anatomy"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: DUR.slow, ease: EASE_OUT_QUINT }}
              ref={figureRef}
              style={{ position: 'relative' }}
            >
              <Ribbon fields={anatomy.fields} hoverId={activeId} />
              <Legend
                fields={anatomy.fields}
                hoverId={activeId}
                setHoverId={setHoverId}
                pinnedId={pinnedId}
                togglePinned={togglePinned}
              />
              <LeaderLine wrapperRef={figureRef} hoverId={activeId} />
              {anatomy.warnings.length > 0 && (
                <div
                  className="t-mono"
                  style={{
                    marginTop: 32,
                    fontSize: 'var(--type-xs)',
                    color: 'color-mix(in oklch, var(--plate-text) 65%, transparent)',
                    letterSpacing: 'var(--tracking-mono-caps)',
                    textTransform: 'uppercase',
                  }}
                >
                  {anatomy.warnings.map((w, i) => (
                    <div key={i}>// {w}</div>
                  ))}
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="skeleton"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ marginTop: 56 }}
            >
              <div
                style={{
                  height: 64,
                  borderRadius: 8,
                  background: 'color-mix(in oklch, var(--plate-text) 12%, transparent)',
                  animation: 'shimmer 1.6s var(--ease-in-out-cubic) infinite',
                }}
              />
              <p
                className="t-mono"
                style={{
                  marginTop: 24,
                  fontSize: 'var(--type-xs)',
                  color: 'color-mix(in oklch, var(--plate-text) 60%, transparent)',
                  letterSpacing: 'var(--tracking-mono-caps)',
                  textTransform: 'uppercase',
                }}
              >
                {wasm.status === 'error'
                  ? '// failed to load wasm — anatomy needs the engine'
                  : '// building specimen archive…'}
              </p>
              <style>{`
                @keyframes shimmer {
                  0%,100% { opacity: 0.5; }
                  50%     { opacity: 1; }
                }
              `}</style>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      </div>
    </section>
  );
}

/* ── Ribbon ─────────────────────────────────────────────────────────── */

/**
 * Horizontal byte strip. Each field is a contiguous run of small cells.
 * Hovering a legend row sets `hoverId` → that run pulses brighter.
 */
function Ribbon({ fields, hoverId }: { fields: SlbzField[]; hoverId: string | null }) {
  // Idle flicker — when nothing is hovered/pinned, pick a random byte
  // every ~3.5s and flash it briefly. Reads as "alive but not demanding."
  // Reduced-motion users see nothing.
  const reduced = useReducedMotion() ?? false;
  const [flicker, setFlicker] = useState<{ fieldId: string; idx: number } | null>(null);
  useEffect(() => {
    if (reduced) return;
    if (hoverId) {
      setFlicker(null);
      return;
    }
    let clearT: ReturnType<typeof setTimeout> | null = null;
    const interval = setInterval(() => {
      // Pick a random field then a random byte within it (cap at 32 to
      // match the rendered slice). Skip fields with zero bytes.
      const candidates = fields.filter((f) => f.bytes.length > 0);
      if (!candidates.length) return;
      const f = candidates[Math.floor(Math.random() * candidates.length)];
      const max = Math.min(32, f.bytes.length);
      const idx = Math.floor(Math.random() * max);
      setFlicker({ fieldId: f.id, idx });
      if (clearT) clearTimeout(clearT);
      clearT = setTimeout(() => setFlicker(null), 240);
    }, 3500);
    return () => {
      clearInterval(interval);
      if (clearT) clearTimeout(clearT);
    };
  }, [hoverId, reduced, fields]);

  return (
    <div
      style={{
        marginTop: 56,
        padding: '32px 24px',
        border: '1px solid color-mix(in oklch, var(--plate-text) 25%, transparent)',
        borderRadius: 12,
        background: 'color-mix(in oklch, var(--plate-text) 5%, transparent)',
        overflowX: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 4,
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
        }}
      >
        {fields.map((f) => (
          <FieldGroup
            key={f.id}
            field={f}
            active={hoverId === f.id}
            dim={hoverId !== null && hoverId !== f.id}
            flickerIdx={flicker && flicker.fieldId === f.id ? flicker.idx : -1}
          />
        ))}
      </div>
    </div>
  );
}

function FieldGroup({
  field,
  active,
  dim,
  flickerIdx,
}: {
  field: SlbzField;
  active: boolean;
  dim: boolean;
  flickerIdx: number;
}) {
  return (
    <div
      data-field-ribbon={field.id}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 6,
        opacity: dim ? 0.35 : 1,
        transition: 'opacity var(--dur-fast) var(--ease-out-expo)',
      }}
    >
      {/* Tiny field label above the byte run */}
      <span
        style={{
          fontSize: 9,
          letterSpacing: 'var(--tracking-mono-caps)',
          textTransform: 'uppercase',
          color: active
            ? 'var(--plate-text)'
            : 'color-mix(in oklch, var(--plate-text) 55%, transparent)',
          whiteSpace: 'nowrap',
          paddingLeft: 2,
        }}
      >
        {field.label}
      </span>
      <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        {[...field.bytes].slice(0, 32).map((b, i) => (
          <ByteCell
            key={i}
            byte={b}
            group={field.group}
            active={active}
            flicker={i === flickerIdx}
          />
        ))}
        {field.truncated && (
          <span
            style={{
              padding: '4px 10px',
              fontSize: 10,
              color: active
                ? 'var(--plate-text)'
                : 'color-mix(in oklch, var(--plate-text) 60%, transparent)',
              background: active
                ? 'color-mix(in oklch, var(--plate-text) 14%, transparent)'
                : 'color-mix(in oklch, var(--plate-text) 7%, transparent)',
              border: '1px solid color-mix(in oklch, var(--plate-text) 22%, transparent)',
              borderRadius: 4,
              transition: 'all var(--dur-fast) var(--ease-out-expo)',
            }}
          >
            {field.display}
          </span>
        )}
      </div>
    </div>
  );
}

function ByteCell({
  byte,
  group,
  active,
  flicker,
}: {
  byte: number;
  group: SlbzField['group'];
  active: boolean;
  flicker: boolean;
}) {
  // Tonal split between groups so the ribbon reads in three bands at a glance.
  const baseAlpha = group === 'header' ? 0.16 : group === 'entry' ? 0.1 : 0.06;
  const alpha = active || flicker ? Math.min(0.42, baseAlpha + 0.22) : baseAlpha;
  const borderAlpha = active || flicker ? 0.55 : 0.25;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 24,
        height: 24,
        padding: '0 4px',
        fontSize: 10,
        fontWeight: 500,
        letterSpacing: 0.4,
        color: active || flicker
          ? 'var(--plate-text)'
          : 'color-mix(in oklch, var(--plate-text) 80%, transparent)',
        background: `color-mix(in oklch, var(--plate-text) ${(alpha * 100).toFixed(0)}%, transparent)`,
        border: `1px solid color-mix(in oklch, var(--plate-text) ${(borderAlpha * 100).toFixed(0)}%, transparent)`,
        borderRadius: 4,
        // Quick in (cell pops on flicker), gentle out (fades back).
        transition: flicker
          ? 'background 120ms var(--ease-out-expo), border-color 120ms var(--ease-out-expo), color 120ms var(--ease-out-expo)'
          : 'background 500ms var(--ease-out-expo), border-color 500ms var(--ease-out-expo), color 500ms var(--ease-out-expo)',
      }}
    >
      {byte.toString(16).padStart(2, '0').toUpperCase()}
    </span>
  );
}

/* ── Legend ────────────────────────────────────────────────────────── */

function Legend({
  fields,
  hoverId,
  setHoverId,
  pinnedId,
  togglePinned,
}: {
  fields: SlbzField[];
  hoverId: string | null;
  setHoverId: (id: string | null) => void;
  pinnedId: string | null;
  togglePinned: (id: string) => void;
}) {
  const grouped = {
    header: fields.filter((f) => f.group === 'header'),
    entry: fields.filter((f) => f.group === 'entry'),
    data: fields.filter((f) => f.group === 'data'),
  };

  return (
    <div
      style={{
        marginTop: 48,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 32,
      }}
    >
      <Group title="File header · 12 bytes" rows={grouped.header} hoverId={hoverId} setHoverId={setHoverId} pinnedId={pinnedId} togglePinned={togglePinned} />
      <Group title="Entry · variable" rows={grouped.entry} hoverId={hoverId} setHoverId={setHoverId} pinnedId={pinnedId} togglePinned={togglePinned} />
      <Group title="Compressed stream" rows={grouped.data} hoverId={hoverId} setHoverId={setHoverId} pinnedId={pinnedId} togglePinned={togglePinned} />
    </div>
  );
}

function Group({
  title,
  rows,
  hoverId,
  setHoverId,
  pinnedId,
  togglePinned,
}: {
  title: string;
  rows: SlbzField[];
  hoverId: string | null;
  setHoverId: (id: string | null) => void;
  pinnedId: string | null;
  togglePinned: (id: string) => void;
}) {
  return (
    <div>
      <div
        className="t-mono"
        style={{
          fontSize: 'var(--type-xs)',
          color: 'color-mix(in oklch, var(--plate-text) 60%, transparent)',
          letterSpacing: 'var(--tracking-mono-caps)',
          textTransform: 'uppercase',
          paddingBottom: 12,
          borderBottom: '1px solid color-mix(in oklch, var(--plate-text) 20%, transparent)',
          marginBottom: 12,
        }}
      >
        {title}
      </div>
      <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {rows.map((f) => (
          <LegendRow
            key={f.id}
            field={f}
            active={hoverId === f.id}
            pinned={pinnedId === f.id}
            onEnter={() => setHoverId(f.id)}
            onLeave={() => setHoverId(null)}
            onToggle={() => togglePinned(f.id)}
          />
        ))}
      </ul>
    </div>
  );
}

function LegendRow({
  field,
  active,
  pinned,
  onEnter,
  onLeave,
  onToggle,
}: {
  field: SlbzField;
  active: boolean;
  pinned: boolean;
  onEnter: () => void;
  onLeave: () => void;
  onToggle: () => void;
}) {
  return (
    <li
      data-field-legend={field.id}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={onLeave}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      role="button"
      aria-pressed={pinned}
      tabIndex={0}
      style={{
        padding: '12px 14px',
        borderRadius: 8,
        background: active
          ? 'color-mix(in oklch, var(--plate-text) 10%, transparent)'
          : 'transparent',
        outline: pinned
          ? '1px solid color-mix(in oklch, var(--plate-text) 35%, transparent)'
          : '1px solid transparent',
        outlineOffset: -1,
        cursor: 'pointer',
        transition:
          'background var(--dur-fast) var(--ease-out-expo), outline-color var(--dur-fast) var(--ease-out-expo)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <span
          style={{
            fontSize: 'var(--type-sm)',
            fontWeight: 500,
            color: 'var(--plate-text)',
          }}
        >
          {field.label}
        </span>
        <span
          className="t-mono"
          style={{
            fontSize: 11,
            color: 'color-mix(in oklch, var(--plate-text) 70%, transparent)',
          }}
        >
          {field.display}
        </span>
      </div>
      <p
        style={{
          marginTop: 4,
          fontSize: 'var(--type-sm)',
          color: 'color-mix(in oklch, var(--plate-text) 75%, transparent)',
          lineHeight: 1.45,
        }}
      >
        {field.hint}
      </p>
      <div
        className="t-mono"
        style={{
          marginTop: 6,
          fontSize: 10,
          color: 'color-mix(in oklch, var(--plate-text) 50%, transparent)',
          letterSpacing: 'var(--tracking-mono-caps)',
          textTransform: 'uppercase',
        }}
      >
        offset 0x{field.offset.toString(16).padStart(2, '0').toUpperCase()} · {field.length}{' '}
        byte{field.length === 1 ? '' : 's'}
      </div>
    </li>
  );
}

/* ── Leader line (GSAP-drawn) ───────────────────────────────────────── */

/**
 * Curved coral hairline that connects the hovered legend row to its
 * matching ribbon segment. Draws in via GSAP stroke-dashoffset; fades
 * out on leave. Cleaner than synced opacity dimming alone — earns the
 * "exploded blueprint" framing of the section.
 */
function LeaderLine({
  wrapperRef,
  hoverId,
}: {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  hoverId: string | null;
}) {
  const pathRef = useRef<SVGPathElement>(null);
  const dotRibbonRef = useRef<SVGCircleElement>(null);
  const dotLegendRef = useRef<SVGCircleElement>(null);

  useEffect(() => {
    const path = pathRef.current;
    const dotR = dotRibbonRef.current;
    const dotL = dotLegendRef.current;
    if (!path || !dotR || !dotL) return;

    if (!hoverId || !wrapperRef.current) {
      gsap.to([path, dotR, dotL], { opacity: 0, duration: DUR.fast });
      return;
    }

    const wrap = wrapperRef.current.getBoundingClientRect();
    const ribbonEl = wrapperRef.current.querySelector<HTMLElement>(
      `[data-field-ribbon="${hoverId}"]`,
    );
    const legendEl = wrapperRef.current.querySelector<HTMLElement>(
      `[data-field-legend="${hoverId}"]`,
    );
    if (!ribbonEl || !legendEl) return;

    const rb = ribbonEl.getBoundingClientRect();
    const lg = legendEl.getBoundingClientRect();

    // Anchor: bottom-center of the ribbon field group
    const x1 = rb.left + rb.width / 2 - wrap.left;
    const y1 = rb.bottom - wrap.top;
    // Anchor: top-left of the legend row, just inside the padding
    const x2 = lg.left + 14 - wrap.left;
    const y2 = lg.top - wrap.top;

    const midY = y1 + (y2 - y1) * 0.55;
    const d = `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`;
    path.setAttribute('d', d);
    dotR.setAttribute('cx', String(x1));
    dotR.setAttribute('cy', String(y1));
    dotL.setAttribute('cx', String(x2));
    dotL.setAttribute('cy', String(y2));

    const length = path.getTotalLength();
    gsap.set(path, {
      strokeDasharray: length,
      strokeDashoffset: length,
      opacity: 1,
    });
    gsap.set([dotR, dotL], { opacity: 0 });
    gsap.to(path, {
      strokeDashoffset: 0,
      duration: DUR.slow,
      ease: GSAP_EASE.outQuart,
    });
    gsap.to([dotR, dotL], {
      opacity: 1,
      duration: DUR.fast,
      delay: DUR.base,
    });
  }, [hoverId, wrapperRef]);

  return (
    <svg
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        overflow: 'visible',
      }}
    >
      <path
        ref={pathRef}
        stroke="var(--plate-text)"
        strokeOpacity={0.5}
        strokeWidth={1.5}
        fill="none"
        strokeLinecap="round"
        opacity={0}
      />
      <circle ref={dotRibbonRef} r={2.5} fill="var(--plate-text)" opacity={0} />
      <circle ref={dotLegendRef} r={2.5} fill="var(--plate-text)" opacity={0} />
    </svg>
  );
}
